/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {BooleanSelectionList} from "@app/models/boolean-selection-list";
import {ConfigSectionComponent} from "@app/models/components/config-section.component";
import {RegistryContexts} from "@app/models/constants/registry-constants";
import {AppSelection} from "@app/models/selection";
import {CommonState} from "@app/store/common/common.state";
import {PatientTreatmentPlanAdvState} from "@doctor-panel-modules/patient-treatment-plan/store/patient-treatment-plan-adv.state";
import {hasActionsExecuting} from "@ngxs-labs/actions-executing";
import {Select, Store} from "@ngxs/store";
import {TrecTranslation} from "@trec/api/common";
import {Feature, Key2, KeyValue} from "@trec/api/registry";
import {StoreAction} from "@trec/components/store";
import {BehaviorSubject, map, Observable, Subscription} from "rxjs";
import {TpAdvActions} from "../../../store/patient-treatment-plan-adv.actions";

@Component({
  selector: "c3-config-app-features",
  templateUrl: "./config-app-features.component.html",
  styleUrls: ["./config-app-features.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ConfigAppFeaturesComponent
  extends ConfigSectionComponent
  implements OnInit, OnDestroy {
  // Data /////////////////////////////////////////////////////////////////////////////////////////

  @Select(hasActionsExecuting([TpAdvActions.FetchAppFeatures, TpAdvActions.UpdateAppFeatures]))
  loading$: Observable<boolean>;
  @Select(PatientTreatmentPlanAdvState.availableAppFeaturesByContext)
  featuresByContext$: Observable<Record<string, (Feature & TrecTranslation)[]>>;

  filteredContexts$: Observable<Record<string, (Feature & TrecTranslation)[]>>;

  @Input()
  context: RegistryContexts[];

  // Selection //////////////////////////////////
  selection: AppSelection = {};

  private valid = new BehaviorSubject(false);
  private touched = new BehaviorSubject(false);

  get touched$(): Observable<boolean> {
    return this.touched.asObservable();
  }

  get valid$(): Observable<boolean> {
    return this.valid.asObservable();
  }

  /**
   * Subscription and selection list to set all app features' values load by the server as selected
   */
  private subscr: Subscription;

  constructor(
    private store: Store,
    private cd: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit() {
    // Fetch app features
    this.store.dispatch(new TpAdvActions.FetchAppFeatures());

    const doctorContexts = JSON.parse(localStorage.getItem("CONTEXTS_INFO") || "");

    this.filteredContexts$ =
      this.featuresByContext$
        .pipe(
          map((allContexts) => {
            const filtered: Record<string, (Feature & TrecTranslation)[]> = {};
            const doctorKeys = Object.keys(doctorContexts);

            Object.entries(allContexts || {}).forEach(([key, value]) => {
              if (doctorKeys.includes(key as RegistryContexts)) {
                filtered[key] = value;
              }
            })
            return filtered;
          })
        )


    // As default select all app features
    this.subscr = this.store
      .select(CommonState.patientUserEntryByContext)
      .subscribe((userEntry) => {
        if (userEntry) {
          let keys: Key2<KeyValue>[] = [];
          userEntry.forEach((ueKeys) => {
            if (ueKeys != null) {
              keys = keys.concat(ueKeys.getKeys());
            }
          });

          // Set as selected all features contained in the user entry
          this.selection = keys.reduce((a, f) => ({...a, [f.name]: true}), {});

          // Once features has been loaded -> config is automatically valid
          this.valid.next(true);

          this.cd.markForCheck();
        }
      });
  }

  /**
   * Callback called when user select/deselect a feature.
   *
   * @param event Event
   */
  onSelectionChange(event: CustomEvent) {
    this.touched.next(true);

    this.selection = {...this.selection, [event.detail.value]: !!event.detail.checked};
  }

  onSelectAll() {
    const appFeatureObj =
      this.store.selectSnapshot(PatientTreatmentPlanAdvState.availableAppFeaturesByContext) ?? [];
    const appFeature = Array.isArray(appFeatureObj)
      ? appFeatureObj
      : Object.values(appFeatureObj).flat();
    this.selection = appFeature.reduce((a, f) => ({...a, [f._id]: true}), {});

    this.touched.next(true);
  }

  onDeselectAll() {
    this.selection = {};

    this.touched.next(true);
  }

  prepareSaveDataAction(): StoreAction | null {
    // Create list of selected app features
    const featuresByContext = this.store.selectSnapshot(
      PatientTreatmentPlanAdvState.availableAppFeaturesByContext,
    );

    if (!featuresByContext) {
      throw new Error(
        `Try to update app features of a patient but available app features list is missing`,
      );
    }

    // Estrarre tutte le features da tutti i contesti
    const allFeatures = Object.values(featuresByContext).flat();

    const updatedSelectedFeatures: BooleanSelectionList<Feature> = allFeatures.map((f) => ({
      item: f,
      selected: this.selection[f.id],
    }));

    return new TpAdvActions.UpdateAppFeatures({appFeatures: updatedSelectedFeatures});
  }

  resetTouched(): void {
    this.touched.next(false);
  }

  ngOnDestroy() {
    this.subscr?.unsubscribe();

    this.valid.complete();
    this.touched.complete();
  }
}
